<template>
  <BaseWidget
    title="Most enrolled automations"
    :show-info-icon="true"
    info-tooltip="Automations ranked by enrolment count within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9500741-what-is-an-automation', text: 'Learn about Automations' }"
    :automations-count="false"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <div
        v-if="focused"
        class="focused-kpi"
      >
        <span class="focused-count">{{ totalEnrollmentsCount }}</span>
        <div class="focused-label-row">
          <span class="focused-label">enrolments · {{ dateRangeLabel }}</span>
          <a
            v-if="topAutomations.length > 0"
            class="focused-view-all-link"
            @click.prevent="handleViewAllClick"
          >
            View all
          </a>
        </div>
      </div>
      <div
        v-else
        class="enrolments-container"
      >
        <div
          v-if="allAutomations.length === 0"
          class="no-data"
        >
          No automation enrolments found
        </div>
        <template v-else>
          <!-- Stacked bar using ALL automations -->
          <div class="stacked-bar-wrapper">
            <div class="stacked-bar">
              <div
                v-for="(automation, index) in allAutomations"
                :key="automation.workflowId || index"
                class="stacked-bar-segment"
                :style="{
                  width: `${getSegmentWidth(automation.count)}%`,
                  backgroundColor: getBarColor(index)
                }"
                @mouseenter="(e) => showSegmentTooltip(e, automation)"
                @mouseleave="hideSegmentTooltip"
                @click="automation.workflowId ? openWorkflowGuarded(automation.workflowId) : null"
              />
            </div>
          </div>
          <div class="date-range-label">
            <span>{{ dateRangeLabel }}</span>
            <a
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
          <!-- Legend: top 3 only, two-line rows (name + enrolment count as meta) -->
          <div class="enrolments-list">
            <div
              v-for="(automation, index) in topAutomations"
              :key="automation.workflowId || index"
              class="enrolment-row"
              @click="automation.workflowId ? openWorkflowGuarded(automation.workflowId) : null"
            >
              <span
                class="legend-color-swatch"
                :style="{ backgroundColor: getBarColor(index) }"
              />
              <div class="enrolment-info">
                <span class="enrolment-name">{{ automation.name }}</span>
                <span class="enrolment-meta">{{ automation.count }} enrolments</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="segmentTooltip.show && segmentTooltip.position"
        ref="segmentTooltipRef"
        class="stacked-bar-tooltip"
        :style="segmentTooltipStyle"
      >
        {{ segmentTooltip.text }}
      </div>
    </Teleport>
  </BaseWidget>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import BaseWidget from './BaseWidget.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useMostPopularAutomationsData } from '../composables/useWidgetData.js'
import { getDateRangeTimestamps, DATE_RANGE_LABELS } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const dateRange = ref(props.props?.dateRange || 'last_7_days')

const timestamps = computed(() => {
  return getDateRangeTimestamps(dateRange.value)
})

const dateRangeLabel = computed(() => DATE_RANGE_LABELS[dateRange.value] || 'Last 7 days')

const { data, loading, error, cleanup, refresh } = useMostPopularAutomationsData({
  priority: 1,
  variables: computed(() => ({
    timestamp_from: timestamps.value.from_timestamp,
    timestamp_to: timestamps.value.to_timestamp
  }))
})

// All automations with enrolments, sorted by count (for stacked bar)
const allAutomations = computed(() => {
  if (!data.value?.workflows) return []

  return data.value.workflows
    .filter(w => w.enrollmentCount > 0)
    .map(w => ({
      workflowId: w.workflowId,
      name: w.name,
      count: w.enrollmentCount
    }))
    .sort((a, b) => b.count - a.count)
})

// Top 3 for the legend table
const topAutomations = computed(() => {
  return allAutomations.value.slice(0, 3)
})

const totalEnrollmentsCount = computed(() => {
  return allAutomations.value.reduce((sum, a) => sum + a.count, 0)
})

// Stacked bar segment width
const getSegmentWidth = (count) => {
  const total = totalEnrollmentsCount.value
  if (total === 0) return 0
  return (count / total) * 100
}

// Distinct colors per automation
const AUTOMATION_COLORS = ['#9B8EC2', '#5B9BD5', '#70AD97', '#ED7D31', '#C55A5A', '#6C8EBF', '#B5A642', '#D46A91', '#4DBFA5', '#8B6EB0']
const getBarColor = (index) => {
  return AUTOMATION_COLORS[index % AUTOMATION_COLORS.length]
}

// Tooltip state
const segmentTooltipRef = ref(null)
const segmentTooltip = ref({ show: false, text: '', position: null })

const segmentTooltipStyle = computed(() => {
  if (!segmentTooltip.value.position) return {}
  const { left, top } = segmentTooltip.value.position
  return {
    left: `${left}px`,
    top: `${top}px`,
    transform: 'translateX(-50%)'
  }
})

const showSegmentTooltip = (e, automation) => {
  const el = e.currentTarget
  const rect = el.getBoundingClientRect()
  const tooltipHeight = 36
  const margin = 6
  segmentTooltip.value = {
    show: true,
    text: `${automation.name} – ${automation.count} enrolments`,
    position: {
      left: rect.left + rect.width / 2,
      top: rect.top - tooltipHeight - margin
    }
  }
  nextTick(() => {
    if (segmentTooltipRef.value) {
      const h = segmentTooltipRef.value.offsetHeight
      segmentTooltip.value = {
        ...segmentTooltip.value,
        position: {
          left: rect.left + rect.width / 2,
          top: rect.top - h - margin
        }
      }
    }
  })
}

const hideSegmentTooltip = () => {
  segmentTooltip.value = { show: false, text: '', position: null }
}

const handleTooltipScroll = () => {
  if (segmentTooltip.value.show) hideSegmentTooltip()
}

const handleTooltipResize = () => {
  if (segmentTooltip.value.show) hideSegmentTooltip()
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  let clientId = ''
  let canvasUUID = ''

  const urlParams = new URLSearchParams(window.location.search)
  clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''

  if (!canvasUUID && window.name) {
    const nameMatch = window.name.match(/^\d+-\d+$/)
    if (nameMatch) {
      canvasUUID = window.name
    }
  }

  if (!clientId || !canvasUUID) {
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer)
        const referrerParams = new URLSearchParams(referrerUrl.search)
        if (!clientId) {
          clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
        }
      } catch (_e) {
        // Ignore referrer parse errors
      }
    }
  }

  return { clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const openWorkflowGuarded = guard((workflowId) => {
  if (!workflowId) return

  const params = getUrlParams()
  let canvasUUID = params.canvasUUID

  if (!canvasUUID) {
    const currentUrl = window.location.href
    const urlMatch = currentUrl.match(/canvasUUID[=:](\d+-\d+)/i) || currentUrl.match(/(\d{13}-\d{6})/)
    if (urlMatch) {
      canvasUUID = urlMatch[1]
    }
  }

  if (!canvasUUID && window.name) {
    const nameMatch = window.name.match(/^\d+-\d+$/)
    if (nameMatch) {
      canvasUUID = window.name
    }
  }

  const generateCanvasUUID = () => {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
    return `${timestamp}-${random}`
  }

  const finalCanvasUUID = canvasUUID || generateCanvasUUID()

  const directMessage = {
    workflowId: workflowId.toString(),
    canvasUUID: finalCanvasUUID,
    client_id: params.clientId || undefined
  }

  const commandMessage = {
    command: 'WorkflowView',
    payload: directMessage
  }

  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(directMessage, "*")
      setTimeout(() => {
        window.parent.postMessage(commandMessage, "*")
      }, 50)
      if (window.top && window.top !== window && window.top !== window.parent) {
        window.top.postMessage(directMessage, "*")
        setTimeout(() => {
          window.top.postMessage(commandMessage, "*")
        }, 50)
      }
    }
  } catch (error) {
    console.error('Error posting WorkflowView message:', error)
  }
})

const handleViewAllClick = () => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || "default-canvas-uuid"
  const navigationUrl = "/automations"

  const message = {
    command: 'ReplaceUrl',
    payload: {
      client_id: params.clientId || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Error posting ReplaceUrl message:', e)
  }
}

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

watch(dateRange, () => {
  refresh(true)
})

onMounted(() => {
  window.addEventListener('scroll', handleTooltipScroll, true)
  window.addEventListener('resize', handleTooltipResize)
})

onUnmounted(() => {
  cleanup()
  window.removeEventListener('scroll', handleTooltipScroll, true)
  window.removeEventListener('resize', handleTooltipResize)
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.enrolments-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-height: 0;
  padding: 8px 0 0 0;
}

/* Stacked bar */
.stacked-bar-wrapper {
  width: 100%;
  flex-shrink: 0;
}

.stacked-bar {
  display: flex;
  gap: 1px;
  width: 100%;
  height: 16px;
  border-radius: 4px;
  overflow: visible;
  background-color: #f3f4f6;
}

.stacked-bar-segment {
  height: 100%;
  min-width: 2px;
  transition: opacity 0.2s ease;
  cursor: pointer;
  position: relative;
}

.stacked-bar-segment:first-child {
  border-radius: 4px 0 0 4px;
}

.stacked-bar-segment:last-child {
  border-radius: 0 4px 4px 0;
}

.stacked-bar-segment:hover {
  opacity: 0.9;
}

/* Teleported tooltip */
.stacked-bar-tooltip {
  position: fixed;
  padding: 6px 10px;
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10010;
}

.date-range-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  flex-shrink: 0;
  margin-top: 6px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Legend rows */
.enrolments-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 0 0 auto;
}

.enrolment-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 64px;
  flex-shrink: 0;
  padding: 10px 4px 10px 0;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.enrolment-row:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.enrolment-row:last-child {
  border-bottom: none;
}

.enrolment-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.legend-color-swatch {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.enrolment-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.enrolment-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.enrolment-meta {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.focused-kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.focused-count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
}

.focused-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.focused-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}
</style>
